import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash, EnvelopeSimple, Phone, Buildings, ChatCircle, CheckCircle } from "@phosphor-icons/react";
import { fmtCurrency } from "@/lib/format";
import { PRESET_BY_ID } from "@/lib/presets";
import BillingCard from "@/components/BillingCard";
import { LeadAnalytics } from "@/components/LeadAnalytics";
import { LeadStageControls, STAGES, STAGE_BADGE } from "@/components/LeadStageControls";
import { LeadNotes } from "@/components/LeadNotes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Leads() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [version, setVersion] = useState(0);
  const [pending, setPending] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`);
      setItems(data);
    } catch (e) {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    if (pending) return;
    setPending(id);
    try {
      const { data } = await axios.patch(`${API}/leads/${id}/status`, { status });
      setItems((p) => p.map((l) => (l.id === id ? data : l)));
      setVersion((v) => v + 1);
      toast.success(`Moved to ${STAGES.find((s) => s.id === status)?.label || status}`);
    } catch (e) {
      toast.error("Could not update lead");
    } finally {
      setPending(null);
    }
  };

  const setPremium = async (id, annual_premium) => {
    if (pending) return;
    setPending(id);
    try {
      const { data } = await axios.patch(`${API}/leads/${id}/premium`, { annual_premium });
      setItems((p) => p.map((l) => (l.id === id ? data : l)));
      setVersion((v) => v + 1);
      toast.success("Premium saved");
    } catch (e) {
      toast.error("Could not save premium");
    } finally {
      setPending(null);
    }
  };

  const addNote = async (id, text) => {
    if (pending) return false;
    setPending(id);
    try {
      const { data } = await axios.post(`${API}/leads/${id}/notes`, { text });
      setItems((p) => p.map((l) => (l.id === id ? data : l)));
      setVersion((v) => v + 1);
      toast.success("Note added");
      return true;
    } catch (e) {
      toast.error("Could not add note");
      return false;
    } finally {
      setPending(null);
    }
  };

  const deleteNote = async (id, noteId) => {
    if (pending) return;
    setPending(id);
    try {
      const { data } = await axios.delete(`${API}/leads/${id}/notes/${noteId}`);
      setItems((p) => p.map((l) => (l.id === id ? data : l)));
      toast.success("Note deleted");
    } catch (e) {
      toast.error("Could not delete note");
    } finally {
      setPending(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success("Lead deleted");
      setItems((p) => p.filter((l) => l.id !== id));
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <main data-testid="leads-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <div className="mb-10">
        <span className="label-eyebrow">Inbound</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
          Prospect leads
        </h1>
        <p className="mt-3 text-brand-ink/70 max-w-2xl">
          Anyone who completes the public 3-minute breakdown lands here. Reach out within a
          business day and convert the teaser into a full advisor session.
        </p>
      </div>

      <BillingCard />

      <LeadAnalytics version={version} />

      {renderLeadsBody({ loading, items, remove, setStatus, setPremium, addNote, deleteNote, pending })}
    </main>
  );
}

function renderLeadsBody({ loading, items, remove, setStatus, setPremium, addNote, deleteNote, pending }) {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 gap-5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-brand-line rounded-md p-6 h-44 animate-pulse" />
        ))}
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div
        data-testid="leads-empty"
        className="bg-white border border-brand-line border-dashed rounded-md p-14 text-center"
      >
        <h3 className="font-serif text-2xl text-brand-forest">No leads yet</h3>
        <p className="mt-3 text-sm text-brand-ink/60 max-w-sm mx-auto">
          Share your <span className="font-semibold">/quick-breakdown</span> link on your website
          or in social posts. Every prospect who completes it shows up here.
        </p>
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {items.map((l) => (
        <div
          key={l.id}
          data-testid={`lead-card-${l.id}`}
          className="bg-white border border-brand-line rounded-md p-6 transition-all hover:-translate-y-0.5 hover:shadow-md"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="label-eyebrow">{l.business_name || "Prospect"}</div>
              <h3 className="mt-2 font-serif text-2xl text-brand-forest leading-tight">
                {l.full_name}
              </h3>
              {l.industry && (
                <span
                  data-testid={`lead-industry-${l.id}`}
                  className="mt-2 inline-flex items-center px-2 py-0.5 rounded bg-brand-forest/10 text-brand-forest text-[10px] font-semibold uppercase tracking-wider"
                >
                  {PRESET_BY_ID[l.industry]?.label || l.industry}
                </span>
              )}
              {l.status && l.status !== "new" && (
                <span
                  data-testid={`lead-status-badge-${l.id}`}
                  className={`mt-2 ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${STAGE_BADGE[l.status] || ""}`}
                >
                  {l.status === "client" && <CheckCircle size={11} weight="fill" />}
                  {STAGES.find((s) => s.id === l.status)?.label || l.status}
                </span>
              )}
            </div>
            <button
              onClick={() => remove(l.id)}
              data-testid={`btn-delete-lead-${l.id}`}
              className="text-brand-muted hover:text-red-700 transition-colors"
              title="Delete"
            >
              <Trash size={18} weight="bold" />
            </button>
          </div>

          <div className="mt-5 space-y-1.5 text-sm">
            {l.email && (
              <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-brand-ink/80 hover:text-brand-forest transition-colors">
                <EnvelopeSimple size={14} weight="duotone" /> {l.email}
              </a>
            )}
            {l.phone && (
              <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-brand-ink/80 hover:text-brand-forest transition-colors">
                <Phone size={14} weight="duotone" /> {l.phone}
              </a>
            )}
            {l.business_name && (
              <div className="flex items-center gap-2 text-brand-ink/70">
                <Buildings size={14} weight="duotone" /> {l.business_name}
              </div>
            )}
          </div>

          <div className="mt-5 pt-5 border-t border-brand-line grid grid-cols-3 gap-3 text-center">
            <Mini label="Trucks" value={l.quick_inputs.num_vehicles} />
            <Mini label="Earnings/yr" value={fmtCurrency(l.quick_inputs.annual_contribution, { compact: true })} />
            <Mini label="Key staff" value={l.quick_inputs.num_key_employees} />
          </div>

          <div className="mt-5 pt-4 border-t border-brand-line flex items-baseline justify-between">
            <div className="flex gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">5-yr gain</div>
                <div className="mt-1 font-serif text-lg text-brand-forest">
                  {fmtCurrency(l.teaser_5yr)}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">10-yr gain</div>
                <div className="mt-1 font-serif text-2xl text-brand-copper">
                  {fmtCurrency(l.teaser_wealth_gain)}
                </div>
              </div>
            </div>
            <span className="text-xs text-brand-muted">
              {new Date(l.created_at).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })}
            </span>
          </div>

          {l.results_summary && Object.keys(l.results_summary).length > 0 && (
            <div data-testid={`lead-results-${l.id}`} className="mt-3 grid grid-cols-2 gap-2">
              <ResultRow label="Tax drag saved" value={l.results_summary.tax_drag_saved} />
              <ResultRow label="Interest recaptured" value={l.results_summary.interest_recaptured} />
              <ResultRow label="Retention value" value={l.results_summary.retention_value} />
              <ResultRow label="IBC cash value" value={l.results_summary.ibc_cash_value} />
            </div>
          )}

          {l.ref && (
            <div className="mt-3 flex items-center gap-2 text-[11px]">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-brand-cream text-brand-forest font-semibold uppercase tracking-wider">
                Source · {l.ref}
              </span>
            </div>
          )}

          {l.notes && (
            <div className="mt-4 p-3 bg-brand-cream/50 rounded-md text-xs text-brand-ink/75 italic flex gap-2">
              <ChatCircle size={14} weight="duotone" className="shrink-0 mt-0.5" />
              {l.notes}
            </div>
          )}

          <LeadStageControls
            lead={l}
            onStage={setStatus}
            onPremium={setPremium}
            busy={pending === l.id}
          />
          <LeadNotes
            lead={l}
            onAdd={addNote}
            onDelete={deleteNote}
            busy={pending === l.id}
          />
        </div>
      ))}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.16em] text-brand-muted">{label}</div>
      <div className="mt-1 font-serif text-lg text-brand-forest">{value}</div>
    </div>
  );
}

function ResultRow({ label, value }) {
  if (value === undefined || value === null) return null;
  return (
    <div className="flex justify-between items-baseline bg-brand-cream/40 rounded px-2.5 py-1.5 text-xs">
      <span className="text-brand-ink/60">{label}</span>
      <span className="font-semibold tabular-nums text-brand-forest">{fmtCurrency(value)}</span>
    </div>
  );
}
